import { toCalendarDate } from "@internationalized/date";
import { TimedFormDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { ComponentProps } from "react";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { DateRangePicker, Field, Flex } from "shared/components/Spor";
import { EndsWith } from "shared/utils/objectUtils";
import styled from "styled-components";

type DateRangePickerProps = Omit<
  ComponentProps<typeof DateRangePicker>,
  "variant"
>;
export type DateTimeRangePickerProps<T extends FieldValues> =
  DateRangePickerProps & {
    durationsField: FieldPath<T> & EndsWith<"durations">;
  };

const NoCalendarIconOutline = styled.div`
  button[aria-label="Åpne kalender"] {
    outline: none !important;
  }
`;

export const DateTimeRangePicker = <T extends FieldValues>({
  durationsField: _durationsField,
  isDisabled = false,
  ...props
}: DateTimeRangePickerProps<T>) => {
  /**
   * Zod doesnt understand non-const pathes that well, so we
   * trick it to think durationsField is a const "durations".
   */
  const durationsField = _durationsField as "durations";
  type FormWithDuration = { [durationsField]: TimedFormDuration };

  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormWithDuration>();

  const fromTimeField = `${durationsField}.from_time` as const;
  const toTimeField = `${durationsField}.to_time` as const;

  const durations = watch(durationsField);
  const fromTime = watch(fromTimeField);
  const toTime = watch(toTimeField);

  if (!durations) return null; // Wait for defaultForm useEffect

  const fromTimeError = errors[durationsField]?.from_time;
  const toTimeError = errors[durationsField]?.to_time;

  return (
    <Field>
      <NoCalendarIconOutline>
        <Flex direction="column" alignItems="flex-start">
          <Flex
            pointerEvents={isDisabled ? "none" : "auto"}
            opacity={isDisabled ? 0.5 : 1}
            backgroundColor={isDisabled ? "surface.disabled" : undefined}
            borderRadius="sm"
          >
            <DateRangePicker
              variant="core"
              startLabel="Fra"
              endLabel="Til"
              aria-label="Dato"
              isDisabled={isDisabled}
              value={{
                start: fromTime && toCalendarDate(fromTime),
                end: toTime && toCalendarDate(toTime),
              }}
              invalid={Boolean(fromTimeError || toTimeError)}
              onValueChange={(date) => {
                if (isDisabled || !date) {
                  return;
                }
                setValue(fromTimeField, fromTime.set({ ...date.start }), {
                  shouldValidate: true,
                });
                setValue(toTimeField, toTime.set({ ...date.end }), {
                  shouldValidate: true,
                });
              }}
              {...props}
            />
          </Flex>

          {fromTimeError && (
            <RenderErrorInPath
              errors={errors}
              errorPath="durations.from_time"
            />
          )}
          {toTimeError && !fromTimeError && (
            <RenderErrorInPath errors={errors} errorPath="durations.to_time" />
          )}
        </Flex>
      </NoCalendarIconOutline>
    </Field>
  );
};
